import Control from 'ol/control/Control';
import { unByKey } from 'ol/Observable';
import { isCombinedFormatted } from './types.js';
import { DEFAULT_CURSOR_COLOR, DEFAULT_CURSOR_LABEL_CSS, } from './style.js';
/** OpenLayers Control that shows formatted cursor-position indicators. */
export class CursorPositionControl extends Control {
    gridSystem_;
    color_;
    labelCss_;
    guideLine_;
    xIndicator_;
    yIndicator_;
    xLabel_;
    yLabel_;
    xLine_;
    yLine_;
    combinedIndicator_;
    combinedLabel_;
    pointerMoveKey_ = null;
    pointerLeaveHandler_ = null;
    pointerLeaveTarget_ = null;
    container_;
    lastMode_ = 'hidden';
    lastXText_ = '';
    lastYText_ = '';
    lastCombinedText_ = '';
    // Cached guide-line label box metrics, measured only when they can change.
    xLabelH_ = 0;
    yLabelW_ = 0;
    lastPointerCoord_ = null;
    rafId_ = null;
    pendingCoordinate_ = null;
    pendingPixel_ = null;
    pendingMap_ = null;
    constructor(options = {}) {
        const container = document.createElement('div');
        container.style.cssText =
            'position:absolute;inset:0;pointer-events:none;z-index:20;';
        container.setAttribute('aria-hidden', 'true');
        if (options.className)
            container.className = options.className;
        super({ element: container });
        this.container_ = container;
        this.gridSystem_ = options.gridSystem ?? null;
        if (this.gridSystem_ === null)
            container.style.display = 'none';
        this.color_ = options.style?.color ?? DEFAULT_CURSOR_COLOR;
        this.labelCss_ = options.style?.labelCss ?? DEFAULT_CURSOR_LABEL_CSS;
        this.guideLine_ = options.style?.guideLine ?? false;
        const labelCss = (radius, padding, blockDisplay = true) => `${blockDisplay ? 'display:block;' : ''}white-space:nowrap;padding:${padding};border-radius:${radius};background:${this.color_};${this.labelCss_}`;
        const lineCss = (width, height, extra = '') => `${extra}width:${width};height:${height};background:${this.color_};`;
        const indicatorBase = 'position:absolute;top:0;left:0;will-change:transform;transform:translate3d(0,0,0);';
        this.xIndicator_ = document.createElement('div');
        this.xIndicator_.style.cssText = `${indicatorBase}display:none;`;
        this.xLabel_ = document.createElement('span');
        this.xLabel_.style.cssText = labelCss('0 0 3px 3px', '1px 6px');
        this.xLine_ = document.createElement('div');
        this.xLine_.style.cssText = lineCss('1px', '12px', 'margin:0 auto;');
        this.xIndicator_.appendChild(this.xLabel_);
        this.xIndicator_.appendChild(this.xLine_);
        container.appendChild(this.xIndicator_);
        this.yIndicator_ = document.createElement('div');
        this.yIndicator_.style.cssText = `${indicatorBase}display:flex;align-items:center;visibility:hidden;`;
        this.yLabel_ = document.createElement('span');
        this.yLabel_.style.cssText = labelCss('0 3px 3px 0', '1px 6px', false);
        this.yLine_ = document.createElement('div');
        this.yLine_.style.cssText = lineCss('12px', '1px', 'flex-shrink:0;');
        this.yIndicator_.appendChild(this.yLabel_);
        this.yIndicator_.appendChild(this.yLine_);
        container.appendChild(this.yIndicator_);
        this.combinedIndicator_ = document.createElement('div');
        this.combinedIndicator_.style.cssText = `${indicatorBase}display:none;`;
        this.combinedLabel_ = document.createElement('span');
        this.combinedLabel_.style.cssText = labelCss('3px', '2px 8px');
        this.combinedIndicator_.appendChild(this.combinedLabel_);
        container.appendChild(this.combinedIndicator_);
    }
    getGridSystem() {
        return this.gridSystem_;
    }
    /** Activate or deactivate the control. */
    setGridSystem(gridSystem) {
        const wasActive = this.gridSystem_ !== null;
        this.gridSystem_ = gridSystem;
        this.lastXText_ = '';
        this.lastYText_ = '';
        this.lastCombinedText_ = '';
        if (gridSystem === null) {
            this.container_.style.display = 'none';
            this.detach_();
            this.hide_();
            this.lastPointerCoord_ = null;
            return;
        }
        const map = this.getMap();
        if (!wasActive) {
            this.container_.style.display = '';
            if (map)
                this.attach_(map);
        }
        if (map && this.lastPointerCoord_) {
            const pixel = map.getPixelFromCoordinate(this.lastPointerCoord_);
            if (pixel)
                this.update_(this.lastPointerCoord_, pixel, map);
        }
        else {
            this.hide_();
        }
    }
    setMap(map) {
        this.detach_();
        super.setMap(map);
        if (map && this.gridSystem_ !== null)
            this.attach_(map);
        else
            this.hide_();
    }
    attach_(map) {
        this.pointerMoveKey_ = map.on('pointermove', (evt) => {
            this.lastPointerCoord_ = evt.coordinate;
            this.pendingCoordinate_ = evt.coordinate;
            this.pendingPixel_ = evt.pixel;
            this.pendingMap_ = map;
            this.scheduleFlush_();
        });
        const viewport = map.getViewport();
        if (!viewport)
            return;
        this.pointerLeaveTarget_ = viewport;
        this.pointerLeaveHandler_ = () => {
            this.lastPointerCoord_ = null;
            this.cancelFlush_();
            this.hide_();
        };
        viewport.addEventListener('pointerleave', this.pointerLeaveHandler_);
    }
    detach_() {
        if (this.pointerMoveKey_) {
            unByKey(this.pointerMoveKey_);
            this.pointerMoveKey_ = null;
        }
        if (this.pointerLeaveHandler_ && this.pointerLeaveTarget_) {
            this.pointerLeaveTarget_.removeEventListener('pointerleave', this.pointerLeaveHandler_);
            this.pointerLeaveHandler_ = null;
            this.pointerLeaveTarget_ = null;
        }
        this.cancelFlush_();
    }
    scheduleFlush_() {
        if (this.rafId_ !== null)
            return;
        this.rafId_ = requestAnimationFrame(() => {
            this.rafId_ = null;
            const coord = this.pendingCoordinate_;
            const pixel = this.pendingPixel_;
            const map = this.pendingMap_;
            if (coord && pixel && map)
                this.update_(coord, pixel, map);
        });
    }
    cancelFlush_() {
        if (this.rafId_ !== null) {
            cancelAnimationFrame(this.rafId_);
            this.rafId_ = null;
        }
        this.pendingCoordinate_ = null;
        this.pendingPixel_ = null;
        this.pendingMap_ = null;
    }
    update_(coordinate, pixel, map) {
        const gridSystem = this.gridSystem_;
        if (!gridSystem)
            return;
        const [cx, cy] = coordinate;
        if (cx === undefined || cy === undefined)
            return;
        const [px, py] = pixel;
        if (px === undefined || py === undefined)
            return;
        const projection = map.getView().getProjection();
        const coord = [cx, cy];
        if (gridSystem.isValidCoordinate &&
            !gridSystem.isValidCoordinate(coord, projection)) {
            this.hide_();
            return;
        }
        const formatted = gridSystem.formatCoordinate(coord, projection);
        if (isCombinedFormatted(formatted)) {
            if (this.lastMode_ !== 'combined') {
                this.xIndicator_.style.display = 'none';
                this.yIndicator_.style.visibility = 'hidden';
                this.combinedIndicator_.style.display = '';
                this.lastMode_ = 'combined';
            }
            this.combinedIndicator_.style.transform = `translate3d(${px + 12}px, ${py + 12}px, 0)`;
            if (formatted.combined !== this.lastCombinedText_) {
                this.combinedLabel_.textContent = formatted.combined;
                this.lastCombinedText_ = formatted.combined;
            }
        }
        else {
            if (this.lastMode_ !== 'axis') {
                this.combinedIndicator_.style.display = 'none';
                this.xIndicator_.style.display = '';
                this.yIndicator_.style.visibility = 'visible';
                this.lastMode_ = 'axis';
            }
            this.xIndicator_.style.transform = `translate3d(${px}px, 0, 0) translateX(-50%)`;
            this.yIndicator_.style.transform = `translate3d(0, ${py}px, 0) translateY(-50%)`;
            if (formatted.x !== this.lastXText_) {
                this.xLabel_.textContent = formatted.x;
                this.lastXText_ = formatted.x;
            }
            const yTextChanged = formatted.y !== this.lastYText_;
            if (yTextChanged) {
                this.yLabel_.textContent = formatted.y;
                this.lastYText_ = formatted.y;
            }
            if (this.guideLine_) {
                // Reach from the frame edge to the pointer, past each label's box.
                // offsetHeight/Width force a layout flush, so measure only when the box
                // can change: x-label height is fixed, y-label width tracks its text.
                // ponytail: assumes fixed label font/padding; re-measure on restyle if
                // the labels ever change style at runtime.
                if (this.xLabelH_ === 0)
                    this.xLabelH_ = this.xLabel_.offsetHeight;
                if (yTextChanged || this.yLabelW_ === 0)
                    this.yLabelW_ = this.yLabel_.offsetWidth;
                this.xLine_.style.height = `${Math.max(12, py - this.xLabelH_)}px`;
                this.yLine_.style.width = `${Math.max(12, px - this.yLabelW_)}px`;
            }
        }
    }
    hide_() {
        this.xIndicator_.style.display = 'none';
        this.yIndicator_.style.visibility = 'hidden';
        this.combinedIndicator_.style.display = 'none';
        this.lastMode_ = 'hidden';
    }
    disposeInternal() {
        this.detach_();
        super.disposeInternal();
    }
}
//# sourceMappingURL=CursorPositionControl.js.map